import React, { useState, useEffect, useRef } from 'react';
import { apiFetch as api } from '../utils/api';

const API = 'https://whitcomb-dashboard-api-production.up.railway.app';

const HABITS = [
  {
    key: 'meditation',
    label: 'Meditation',
    icon: '🧘',
    unit: 'days',
    weeklyTarget: 5,
    color: '#7B6FA0',
    colorBg: '#F4F2F8',
    colorBorder: '#7B6FA033',
    prompt: 'Minutes meditated',
    placeholder: '10',
    dailyTarget: 10,
    apiType: 'meditation',
  },
  {
    key: 'spanish',
    label: 'Spanish',
    icon: '🇪🇸',
    unit: 'days',
    weeklyTarget: 7,
    color: '#C0392B',
    colorBg: '#FDF0EF',
    colorBorder: '#C0392B33',
    prompt: "Log today's session",
    placeholder: '1',
    isBinary: true,
    streakOffset: 2323,
    apiType: 'habits',
  },
  {
    key: 'read',
    label: 'Reading',
    icon: '📖',
    unit: 'min',
    weeklyTarget: 60,
    color: '#C27D38',
    colorBg: '#FBF5EE',
    colorBorder: '#C27D3833',
    prompt: 'Minutes read today',
    placeholder: '20',
    apiType: 'habits',
  },
  {
    key: 'deepwork',
    label: 'Deep Work',
    icon: '🔥',
    unit: 'hrs',
    weeklyTarget: 4,
    color: '#6B8F6B',
    colorBg: '#EFF5EF',
    colorBorder: '#6B8F6B33',
    prompt: 'Hours of deep work today',
    placeholder: '1.5',
    apiType: 'habits',
  },
];

const ANNUAL_TARGETS = {
  meditation: { target: 5 * 52,  unit: 'days' },
  spanish:    { target: 365,     unit: 'days' },
  read:       { target: 60 * 52, unit: 'min'  },
  deepwork:   { target: 4 * 52,  unit: 'hrs'  },
};

const TABS = HABITS.map(h => ({ id: h.key, label: h.label, icon: h.icon }));

function getWeekStart(date) {
  const d = new Date(date + 'T12:00:00');
  d.setDate(d.getDate() - d.getDay());
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
}

function getDayOfYear(date) {
  const d = new Date(date + 'T12:00:00');
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d - start) / (1000 * 60 * 60 * 24));
}

function computeMeditationStreak(byDate, dailyTarget) {
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 3000; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    if ((byDate[key] || 0) >= dailyTarget) { streak++; }
    else if (i === 0) { continue; }
    else { break; }
  }
  return streak;
}

function getStreak(logs, habitKey, dailyTarget, streakOffset) {
  streakOffset = streakOffset || 0;
  const byDate = {};
  logs.filter(l => l.habit === habitKey).forEach(l => {
    byDate[l.date] = (byDate[l.date] || 0) + parseFloat(l.value);
  });
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 3000; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    if ((byDate[key] || 0) >= (dailyTarget || 1)) { streak++; }
    else if (i === 0) { continue; }
    else { break; }
  }
  return streak + streakOffset;
}

function ProgressRing({ pct, color, size, strokeWidth, label, sublabel }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(1, pct / 100));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#EBEBEB" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
        <div style={{ fontSize: size > 130 ? 48 : 22, fontWeight: 900, color, lineHeight: 1 }}>{label}</div>
        {sublabel && <div style={{ fontSize: 11, color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{sublabel}</div>}
      </div>
    </div>
  );
}

function SpanishFlagHeatmap({ entries }) {
  const today = new Date();
  const days = [];
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split('T')[0]);
  }
  const weeks = [];
  let week = [];
  const firstDay = new Date(days[0]).getDay();
  for (let i = 0; i < firstDay; i++) week.push(null);
  days.forEach(d => {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  });
  if (week.length) weeks.push(week);
  const todayKey = new Date(today.getTime() - today.getTimezoneOffset() * 60000).toISOString().split('T')[0];

  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((today - startOfYear) / 86400000);
  const phase = (dayOfYear / 60) * 2 * Math.PI;

  const RED    = '#C0392B';
  const YELLOW = '#F1C40F';

  const getColor = (colIndex, numCols, rowIndex, done) => {
    if (!done) return '#EBEBEB';
    const x = numCols > 1 ? colIndex / (numCols - 1) : 0;
    const wave = 0.7 * Math.sin(x * 2 * Math.PI + phase);
    const boundary1 = 1.75 + wave;
    const boundary2 = 4.25 + wave;
    if (rowIndex < boundary1) return RED;
    if (rowIndex < boundary2) return YELLOW;
    return RED;
  };

  const numCols = weeks.length;
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'nowrap', overflowX: 'auto' }}>
      {weeks.map((w, wi) => (
        <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {w.map((d, di) => {
            if (!d) return <div key={di} style={{ width: 12, height: 12 }} />;
            const done = (entries[d] || 0) >= 1;
            const isToday = d === todayKey;
            const bg = getColor(wi, numCols, di, done);
            return (
              <div key={d} title={d}
                style={{
                  width: 12, height: 12, borderRadius: 2, flexShrink: 0,
                  background: bg,
                  opacity: done ? 1 : 0.18,
                  border: isToday ? '1.5px solid #172A3A' : 'none',
                }} />
            );
          })}
        </div>
      ))}
    </div>
  );
}

function CalendarHeatmap({ entries, dailyTarget, color }) {
  const today = new Date();
  const days = [];
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    days.push(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0]);
  }
  const weeks = [];
  let week = [];
  const firstDay = new Date(days[0]).getDay();
  for (let i = 0; i < firstDay; i++) week.push(null);
  days.forEach(d => {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  });
  if (week.length) weeks.push(week);
  const todayKey = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'nowrap', overflowX: 'auto' }}>
      {weeks.map((w, wi) => (
        <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {w.map((d, di) => {
            if (!d) return <div key={di} style={{ width: 12, height: 12 }} />;
            const val = entries[d] || 0;
            const done = val >= (dailyTarget || 1);
            const partial = val > 0 && !done;
            const isToday = d === todayKey;
            return (
              <div key={d} title={`${d}: ${val}`}
                style={{
                  width: 12, height: 12, borderRadius: 2, flexShrink: 0,
                  background: done ? color : partial ? `${color}66` : '#EBEBEB',
                  border: isToday ? `1px solid ${color}` : 'none',
                }} />
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Book scanning modal — uses Claude vision API to identify book from photo
function BookScanModal({ onAdd, onClose, apiKey }) {
  const [scanning, setScanning]     = useState(false);
  const [detected, setDetected]     = useState(null); // { title, author }
  const [title, setTitle]           = useState('');
  const [author, setAuthor]         = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');


  const handleImage = async (file) => {
    if (!file) return;
    setScanning(true);
    setError('');
    setDetected(null);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const response = await fetch(`${API}/api/identify-book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
        body: JSON.stringify({ image: base64, media_type: file.type || 'image/jpeg' }),
      });
      const parsed = await response.json();
      setDetected(parsed);
      setTitle(parsed.title || '');
      setAuthor(parsed.author || '');
    } catch (e) {
      setError('Could not scan image. Please enter manually.');
    }
    setScanning(false);
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const today = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
      const res = await fetch(`${API}/api/books`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
        body: JSON.stringify({ title: title.trim(), author: author.trim(), started_date: today, status: 'reading' }),
      });
      const data = await res.json();
      onAdd({ id: data.id, title: title.trim(), author: author.trim(), started_date: today, finished_date: null, status: 'reading' });
      onClose();
    } catch (e) {
      setError('Failed to save book.');
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#172A3A' }}>📖 Add a Book</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#aaa' }}>✕</button>
        </div>

        {/* Camera / upload button */}
        <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', padding: '12px', borderRadius: 10, border: '2px dashed #EBEBEB',
            background: scanning ? '#F0F0F0' : '#FAFAFA', cursor: scanning ? 'default' : 'pointer',
            fontSize: 14, color: '#172A3A', fontWeight: 600, fontFamily: 'inherit',
            marginBottom: 16, boxSizing: 'border-box',
          }}>
          <input type="file" accept="image/*"
            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden' }}
            disabled={scanning}
            onChange={e => { if (e.target.files[0]) { handleImage(e.target.files[0]); e.target.value = ''; } }} />
          {scanning ? '🔍 Scanning...' : '📷 Take Photo or Choose Image'}
        </label>

        {error && <div style={{ fontSize: 12, color: '#B02A2A', marginBottom: 12 }}>{error}</div>}

        {detected && detected.title && (
          <div style={{ background: '#EEF7F2', border: '1px solid #2C7A4B33', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#2C7A4B' }}>
            ✓ Book identified
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Title</div>
          <input value={title} onChange={e => setTitle(e.target.value)}
            placeholder="Enter book title"
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #EBEBEB', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Author</div>
          <input value={author} onChange={e => setAuthor(e.target.value)}
            placeholder="Enter author name"
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #EBEBEB', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </div>

        <button onClick={handleSave} disabled={saving || !title.trim()}
          style={{
            width: '100%', padding: '12px', borderRadius: 99, border: 'none',
            background: title.trim() ? '#172A3A' : '#ccc', color: 'white',
            cursor: title.trim() ? 'pointer' : 'default', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
          }}>
          {saving ? 'Saving...' : 'Add to Reading List'}
        </button>
      </div>
    </div>
  );
}

const sectionLabel = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: '#aaa',
};

export default function Goals({ data, updateData }) {
  const [habitLogs, setHabitLogs]   = useState([]);
  const [medByDate, setMedByDate]   = useState({});
  const [books, setBooks]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [logInput, setLogInput]     = useState({});
  const [showLog, setShowLog]       = useState(null);
  const [logDate, setLogDate]       = useState(new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookCovers, setBookCovers]     = useState({});
  const [saving, setSaving]         = useState(false);
  const [activeTab, setActiveTab]   = useState('meditation');
  const [showBookModal, setShowBookModal] = useState(false);
  const [showAllBooks, setShowAllBooks]   = useState(false);

  const _d = new Date(); const today = _d.getFullYear() + '-' + String(_d.getMonth()+1).padStart(2,'0') + '-' + String(_d.getDate()).padStart(2,'0');
  const year         = new Date().getFullYear();
  const dayOfYear    = getDayOfYear(new Date());
  const yearProgress = dayOfYear / 365;
  const weekStart    = getWeekStart(today);
  const apiKey       = process.env.REACT_APP_API_KEY;

  useEffect(() => {
    if (data.habitLogs) setHabitLogs(data.habitLogs);
    if (data.meditation) {
      const obj = {};
      data.meditation.forEach(r => { obj[r.date] = parseFloat(r.minutes); });
      setMedByDate(obj);
    }
    if (data.books) {
      setBooks(data.books);
      data.books.forEach(book => {
        const fetchCover = (query) =>
          fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=1&fields=cover_i`)
            .then(r => r.json())
            .then(d => {
              const coverId = d.docs?.[0]?.cover_i;
              if (coverId) {
                setBookCovers(prev => ({ ...prev, [book.id]: `https://covers.openlibrary.org/b/id/${coverId}-M.jpg` }));
              } else if (query !== book.title) {
                fetchCover(book.title);
              }
            })
            .catch(() => {});
        fetchCover(`${book.title} ${book.author || ''}`);
      });
    }
    if (data.habitLogs || data.meditation || data.books) setLoading(false);
  }, [data.habitLogs, data.meditation, data.books]);

  const logHabit = async (habit, value) => {
    if (!value && value !== 0) return;
    setSaving(true);
    try {
      if (habit.apiType === 'meditation') {
        const minutes = parseFloat(value) || 10;
        await fetch(`${API}/api/meditation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
          body: JSON.stringify({ date: logDate, minutes }),
        });
        setMedByDate(prev => ({ ...prev, [logDate]: (prev[logDate] || 0) + minutes }));
      } else {
        await api('/api/habits', {
          method: 'POST',
          body: JSON.stringify({ date: today, habit: habit.key, value: parseFloat(value) || 1 }),
        });
        const updated = await api('/api/habits');
        setHabitLogs(updated);
      }
      setShowLog(null);
      setLogInput({});
    } catch (e) {
      alert('Failed to save');
    }
    setSaving(false);
  };

  const markBookFinished = async (book) => {
    const finishedDate = today;
    await fetch(`${API}/api/books/${book.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
      body: JSON.stringify({ status: 'finished', finished_date: finishedDate }),
    });
    setBooks(prev => prev.map(b => b.id === book.id ? { ...b, status: 'finished', finished_date: finishedDate } : b));
  };

  const rateBook = async (id, rating) => {
    await fetch(`${API}/api/books/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
      body: JSON.stringify({ rating }),
    });
    setBooks(prev => prev.map(b => b.id === id ? { ...b, rating } : b));
  };

  const deleteBook = async (id) => {
    await fetch(`${API}/api/books/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey },
    });
    setBooks(prev => prev.filter(b => b.id !== id));
  };

  const getStats = (habit) => {
    if (habit.apiType === 'meditation') {
      const todayVal  = medByDate[today] || 0;
      const todayDone = todayVal >= habit.dailyTarget;
      const weekTotal = Object.keys(medByDate)
        .filter(d => d >= weekStart && d <= today && medByDate[d] >= habit.dailyTarget).length;
      const yearTotal = Object.keys(medByDate)
        .filter(d => d.startsWith(String(year)) && medByDate[d] >= habit.dailyTarget).length;
      const streak = computeMeditationStreak(medByDate, habit.dailyTarget);
      return { todayVal, todayDone, weekTotal, yearTotal, streak };
    }
    const byDate = {};
    habitLogs.filter(l => l.habit === habit.key).forEach(l => {
      byDate[l.date] = (byDate[l.date] || 0) + parseFloat(l.value);
    });
    const todayVal  = byDate[today] || 0;
    const todayDone = habit.isBinary ? todayVal >= 1 : todayVal >= (habit.dailyTarget || 1);
    const weekDates = Object.keys(byDate).filter(d => d >= weekStart && d <= today);
    const weekTotal = habit.isBinary
      ? weekDates.length
      : weekDates.reduce((sum, d) => sum + byDate[d], 0);
    const yearLogs  = habitLogs.filter(l => l.habit === habit.key && l.date.startsWith(String(year)));
    const yearTotal = habit.isBinary
      ? new Set(yearLogs.map(l => l.date)).size
      : yearLogs.reduce((sum, l) => sum + parseFloat(l.value), 0);
    const streak = getStreak(habitLogs, habit.key, habit.dailyTarget, habit.streakOffset);
    return { todayVal, todayDone, weekTotal, yearTotal, streak };
  };

  const formatValue = (habit, val, context) => {
    if (habit.apiType === 'meditation') {
      if (context === 'today') return val >= habit.dailyTarget ? '✓' : val > 0 ? `${Math.round(val)}m` : '—';
      return val === 0 ? '—' : val;
    }
    if (habit.isBinary) return val >= 1 ? '✓' : '—';
    if (val === 0 || val === undefined) return context === 'today' ? '—' : '0';
    if (habit.key === 'read') {
      if (context === 'year') {
        if (val >= 60) {
          const hrs = (val / 60);
          return hrs >= 10 ? `${Math.round(hrs)}h` : `${hrs.toFixed(1)}h`;
        }
        return `${Math.round(val)}m`;
      }
      return Math.round(val);
    }
    return Math.round(val * 10) / 10;
  };

  if (loading) return <div style={{ padding: 48, color: '#aaa', textAlign: 'center' }}>Loading…</div>;

  const readingBooks  = books.filter(b => b.status === 'reading');
  const allFinishedBooks = books.filter(b => b.status === 'finished' && b.finished_date);
  const finishedByYear = allFinishedBooks.reduce((acc, b) => {
    const y = b.finished_date.substring(0, 4);
    if (!acc[y]) acc[y] = [];
    acc[y].push(b);
    return acc;
  }, {});
  const finishedYears = Object.keys(finishedByYear).sort((a, b) => b - a);
  const finishedBooks = allFinishedBooks.filter(b => b.finished_date.startsWith(String(year)));

  return (
    <div>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @media (max-width: 600px) {
          .c2-wrap { flex-direction: column !important; }
          .c2-left { flex: 0 0 100% !important; width: 100% !important; border-right: none !important; border-bottom: 1px solid #EBEBEB !important; padding: 28px 24px !important; }
        }
      `}</style>

      {showBookModal && (
        <BookScanModal
          onAdd={book => {
            setBooks(prev => [book, ...prev]);
            const fetchCover = (query) =>
              fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=1&fields=cover_i`)
                .then(r => r.json())
                .then(d => {
                  const coverId = d.docs?.[0]?.cover_i;
                  if (coverId) {
                    setBookCovers(prev => ({ ...prev, [book.id]: `https://covers.openlibrary.org/b/id/${coverId}-M.jpg` }));
                  } else if (query !== book.title) {
                    fetchCover(book.title);
                  }
                })
                .catch(() => {});
            fetchCover(`${book.title} ${book.author || ''}`);
          }}
          onClose={() => setShowBookModal(false)}
          apiKey={apiKey}
        />
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 28, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              padding: '6px 16px', borderRadius: 99,
              border: activeTab === t.id ? 'none' : '1px solid #EBEBEB',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              background: activeTab === t.id ? '#172A3A' : 'white',
              color: activeTab === t.id ? 'white' : '#172A3A',
              transition: 'all 0.15s',
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {(() => {
        const habit = HABITS.find(h => h.key === activeTab);
        if (!habit) return null;
        const stats     = getStats(habit);
        const annual    = ANNUAL_TARGETS[habit.key];
        const annualPct = Math.min(100, (stats.yearTotal / annual.target) * 100);
        const onPace    = stats.yearTotal >= Math.floor(annual.target * yearProgress);

        const weekRingPct   = Math.min(100, (stats.weekTotal / habit.weeklyTarget) * 100);
        const weekRingLabel = habit.apiType === 'meditation' || habit.isBinary
          ? `${Math.round(stats.weekTotal)}/${habit.weeklyTarget}`
          : habit.key === 'read'
            ? `${Math.round(stats.weekTotal)}m`
            : `${Math.round(stats.weekTotal * 10) / 10}h`;

        const heroNumber = (habit.isBinary || habit.apiType === 'meditation')
          ? (stats.streak > 0 ? stats.streak.toLocaleString() : '—')
          : habit.key === 'read'
            ? `${Math.round(stats.weekTotal)}`
            : `${Math.round(stats.weekTotal * 10) / 10}`;
        const heroSub = (habit.isBinary || habit.apiType === 'meditation')
          ? '🔥 day streak'
          : habit.key === 'read' ? 'min' : 'hrs';

        const heatmapEntries = habit.apiType === 'meditation'
          ? medByDate
          : (() => {
              const byDate = {};
              habitLogs.filter(l => l.habit === habit.key).forEach(l => {
                byDate[l.date] = (byDate[l.date] || 0) + parseFloat(l.value);
              });
              return byDate;
            })();

        return (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', overflow: 'hidden' }}>
            <div className="c2-wrap" style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #EBEBEB' }}>

              {/* LEFT */}
              <div className="c2-left" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '32px 40px', borderRight: '1px solid #EBEBEB', flex: '0 0 320px', width: 320, boxSizing: 'border-box' }}>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 16 }}>
                  {habit.icon} {habit.label} · {habit.weeklyTarget} {habit.unit}/week
                </div>
                <ProgressRing
                  pct={weekRingPct} color={habit.color} size={280} strokeWidth={22}
                  label={weekRingLabel} sublabel="this week"
                />
                <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, letterSpacing: '-2px', color: habit.color, marginTop: 16, marginBottom: 4 }}>
                  {heroNumber}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: habit.color, marginBottom: 20 }}>
                  {heroSub}
                </div>
                <button
                  onClick={() => { setShowLog(showLog === habit.key ? null : habit.key); setLogDate(today); }}
                  style={{
                    padding: '10px 28px', borderRadius: 99, border: 'none',
                    cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                    background: stats.todayDone ? '#EEF7F2' : '#172A3A',
                    color: stats.todayDone ? '#2C7A4B' : 'white',
                    transition: 'all 0.15s',
                  }}>
                  {stats.todayDone ? (habit.isBinary ? '✓ Logged Today' : '+ Log More Today') : '+ Log Today'}
                </button>
                {/* Book scan button — only on Reading tab */}
                {habit.key === 'read' && (
                  <button onClick={() => setShowBookModal(true)}
                    style={{
                      marginTop: 10, padding: '10px 28px', borderRadius: 99,
                      border: '1px solid #EBEBEB', background: 'white',
                      cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                      color: '#172A3A',
                    }}>
                    📷 Add a Book
                  </button>
                )}
              </div>

              {/* RIGHT */}
              <div style={{ flex: 1, minWidth: 0, padding: '28px 28px 24px', boxSizing: 'border-box' }}>
                {showLog === habit.key && (
                  <div style={{ background: '#F8F8F8', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid #EBEBEB' }}>
                    <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>{habit.prompt}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!habit.isBinary && (
                        <input type="number"
                          value={logInput[habit.key] || ''}
                          onChange={e => setLogInput(p => ({ ...p, [habit.key]: e.target.value }))}
                          placeholder={habit.placeholder} step="0.5" autoFocus
                          onKeyDown={e => e.key === 'Enter' && logHabit(habit, logInput[habit.key] || habit.placeholder)}
                          style={{ flex: 1, minWidth: 80, padding: '6px 10px', borderRadius: 6, border: '1px solid #EBEBEB', fontSize: 16 }} />
                      )}
                      {habit.apiType === 'meditation' && (
                        <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)}
                          style={{ flex: 1, minWidth: 120, padding: '6px 10px', borderRadius: 6, border: '1px solid #EBEBEB', fontSize: 13 }} />
                      )}
                      <button onClick={() => logHabit(habit, habit.isBinary ? 1 : (logInput[habit.key] || habit.placeholder))} disabled={saving}
                        style={{ padding: '6px 16px', borderRadius: 99, border: 'none', background: '#2C7A4B', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                        Save
                      </button>
                      <button onClick={() => { setShowLog(null); setSelectedBook(null); }}
                        style={{ padding: '6px 12px', borderRadius: 99, border: '1px solid #EBEBEB', background: 'white', color: '#aaa', cursor: 'pointer', fontSize: 13 }}>
                        ✕
                      </button>
                    </div>
                    {/* Book picker — only on Reading tab */}
                    {habit.key === 'read' && readingBooks.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Reading now</div>
                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch' }}>
                          {readingBooks.map(book => {
                            const isSelected = selectedBook?.id === book.id;
                            const cover = bookCovers[book.id];
                            return (
                              <div key={book.id} onClick={() => setSelectedBook(isSelected ? null : book)}
                                style={{
                                  flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '6px 10px', borderRadius: 8,
                                  background: isSelected ? '#FBF5EE' : 'white',
                                  border: isSelected ? '1.5px solid #C27D38' : '1.5px solid #EBEBEB',
                                  transition: 'all 0.15s',
                                  maxWidth: 200,
                                }}>
                                <div style={{
                                  width: 32, height: 44, borderRadius: 3, overflow: 'hidden',
                                  background: '#E8E0D4', flexShrink: 0,
                                }}>
                                  {cover
                                    ? <img src={cover} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📖</div>
                                  }
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: isSelected ? '#C27D38' : '#172A3A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>
                                    {book.title}
                                  </div>
                                  {book.author && <div style={{ fontSize: 11, color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>{book.author}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
                  {[
                    { label: 'Today',     value: formatValue(habit, stats.todayVal, 'today') },
                    { label: 'This Week', value: formatValue(habit, stats.weekTotal, 'week')  },
                    { label: 'This Year', value: formatValue(habit, stats.yearTotal, 'year')  },
                  ].map(s => (
                    <div key={s.label} style={{ background: habit.colorBg, border: `1.5px solid ${habit.colorBorder}`, borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: habit.color, lineHeight: 1, marginBottom: 4 }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                    <div style={sectionLabel}>Annual Goal</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: onPace ? '#2C7A4B' : '#B8922A' }}>
                      {onPace ? '✓ On pace' : '⚠ Behind pace'}
                    </div>
                  </div>
                  <div style={{ background: '#F0F0F0', borderRadius: 99, height: 8, overflow: 'hidden', marginBottom: 5 }}>
                    <div style={{ width: `${annualPct}%`, height: '100%', background: habit.color, borderRadius: 99, transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>
                    {Math.round(stats.yearTotal * 10) / 10} of {annual.target} {annual.unit}
                  </div>
                </div>

                <div style={{ marginBottom: habit.key === 'read' ? 20 : 0 }}>
                  <div style={{ ...sectionLabel, marginBottom: 8 }}>Last 90 Days</div>
                  {habit.key === 'spanish'
                    ? <SpanishFlagHeatmap entries={heatmapEntries} />
                    : <CalendarHeatmap entries={heatmapEntries} dailyTarget={habit.dailyTarget} color={habit.color} />
                  }
                </div>

                {/* Book list — only on Reading tab */}
                {habit.key === 'read' && (
                  <div>
                    {readingBooks.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ ...sectionLabel, marginBottom: 10 }}>Currently Reading</div>
                        {readingBooks.map(book => (
                          <div key={book.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F0F0F0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                              {bookCovers[book.id]
                                ? <img src={bookCovers[book.id]} alt={book.title} style={{ width: 36, height: 54, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                                : <div style={{ width: 36, height: 54, background: '#F0F0F0', borderRadius: 3, flexShrink: 0 }} />
                              }
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#172A3A' }}>{book.title}</div>
                                {book.author && <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{book.author}</div>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                              <button onClick={() => markBookFinished(book)}
                                style={{ padding: '4px 10px', borderRadius: 99, border: '1px solid #2C7A4B', background: 'white', color: '#2C7A4B', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                                ✓ Done
                              </button>
                              <button onClick={() => deleteBook(book.id)}
                                style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 14 }}>✕</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {allFinishedBooks.length > 0 && (() => {
                      const recent = [...allFinishedBooks].sort((a,b) => b.finished_date.localeCompare(a.finished_date)).slice(0,10);
                      const total  = allFinishedBooks.length;
                      return (
                        <div>
                          <div style={{ ...sectionLabel, marginBottom: 10 }}>Recently Finished</div>
                          {recent.map(book => (
                            <div key={book.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F0F0F0' }}>
                              {bookCovers[book.id]
                                ? <img src={bookCovers[book.id]} alt={book.title} style={{ width: 36, height: 54, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                                : <div style={{ width: 36, height: 54, background: '#F0F0F0', borderRadius: 3, flexShrink: 0 }} />
                              }
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#172A3A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{book.title}</div>
                                {book.author && <div style={{ fontSize: 12, color: '#aaa', marginTop: 1 }}>{book.author}</div>}
                                <div style={{ display: 'flex', gap: 2, marginTop: 3 }}>
                                  {[1,2,3,4,5].map(s => (
                                    <span key={s} onClick={() => rateBook(book.id, s)} style={{ cursor: 'pointer', fontSize: 14, color: s <= (book.rating||0) ? '#E07B00' : '#ddd' }}>★</span>
                                  ))}
                                </div>
                              </div>
                              <div style={{ fontSize: 11, color: '#aaa', flexShrink: 0 }}>
                                {book.finished_date ? new Date(book.finished_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                              </div>
                            </div>
                          ))}
                          {total > 0 && (
                            <button onClick={() => setShowAllBooks(true)} style={{ marginTop: 14, background: 'none', border: 'none', color: '#4A90A4', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
                              See all {total} book{total !== 1 ? 's' : ''} →
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* All Books slide-in panel */}
                    {showAllBooks && (
                      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
                        <div onClick={() => setShowAllBooks(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
                        <div style={{ position: 'relative', width: '100%', maxWidth: 480, background: 'white', height: '100%', overflowY: 'auto', padding: '28px 24px', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', transform: 'translateX(0)', animation: 'slideInRight 0.25s ease-out' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                            <div style={{ fontFamily: 'DM Serif Display, serif', fontSize: 24, color: '#172A3A' }}>My Library</div>
                            <button onClick={() => setShowAllBooks(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: '#aaa', cursor: 'pointer' }}>✕</button>
                          </div>
                          <div style={{ fontSize: 13, color: '#aaa', marginBottom: 24 }}>{allFinishedBooks.length} book{allFinishedBooks.length !== 1 ? 's' : ''} finished</div>
                          {finishedYears.map(y => (
                            <div key={y} style={{ marginBottom: 32 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
                                {y} · {finishedByYear[y].length} book{finishedByYear[y].length !== 1 ? 's' : ''}
                              </div>
                              {[...finishedByYear[y]].sort((a,b) => b.finished_date.localeCompare(a.finished_date)).map(book => (
                                <div key={book.id} style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
                                  {bookCovers[book.id]
                                    ? <img src={bookCovers[book.id]} alt={book.title} style={{ width: 56, height: 84, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                                    : <div style={{ width: 56, height: 84, background: '#F0F0F0', borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📖</div>
                                  }
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#172A3A', lineHeight: 1.3 }}>{book.title}</div>
                                    {book.author && <div style={{ fontSize: 13, color: '#888', marginTop: 3 }}>{book.author}</div>}
                                    <div style={{ display: 'flex', gap: 2, marginTop: 6 }}>
                                      {[1,2,3,4,5].map(s => (
                                        <span key={s} onClick={() => rateBook(book.id, s)} style={{ cursor: 'pointer', fontSize: 18, color: s <= (book.rating||0) ? '#E07B00' : '#ddd' }}>★</span>
                                      ))}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#bbb', marginTop: 4 }}>
                                      {book.finished_date ? new Date(book.finished_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {readingBooks.length === 0 && finishedBooks.length === 0 && (
                      <div style={{ fontSize: 13, color: '#aaa', paddingTop: 4 }}>
                        No books yet — tap "Add a Book" to get started.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
